import { useState, useEffect } from 'react'

import { Add } from '@mui/icons-material'
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid2,
  Hidden,
  TextField,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import { isValid, isValidUser } from './helper'
import { useCreateFellow } from '../../api/fellows'

const initialState = {
  firstName: '',
  lastName: '',
  personalNumber: '',
  email: '',
  phone: '',
  street: '',
  zip: '',
  city: '',
}

const initialTouched = {
  firstName: false,
  lastName: false,
  personalNumber: false,
  email: false,
  phone: false,
  street: false,
  zip: false,
  city: false,
}

export const AddFellow = ({ open, onOpen, onClose, onCreated }) => {
  const { t } = useTranslation()
  const [state, setState] = useState(initialState)
  const [touched, setTouched] = useState(initialTouched)
  const { mutate: createFellow, error, isPending, reset } = useCreateFellow()

  useEffect(() => {
    setState(initialState)
    setTouched(initialTouched)
    reset()
  }, [open, reset])

  function handleChange(event) {
    const { name, value } = event.target
    setState({ ...state, [name]: value })
  }

  function handleBlur(event) {
    const { name } = event.target
    setTouched({ ...touched, [name]: true })
  }

  function handleSubmit(event) {
    event.preventDefault()
    if (isValidUser(state)) {
      if (!isPending) {
        createFellow(state, { onSuccess: onCreated })
      }
    } else {
      setTouched({
        firstName: true,
        lastName: true,
        personalNumber: true,
        email: true,
        phone: true,
        street: true,
        zip: true,
        city: true,
      })
    }
  }

  const {
    firstName,
    lastName,
    personalNumber,
    email,
    phone,
    street,
    zip,
    city,
  } = state

  return (
    <Grid2 size={12}>
      <Button
        onClick={onOpen}
        style={{ textAlign: 'left', marginRight: 32 }}
        endIcon={<Add />}
      >
        {t('Add co-applicant')}
      </Button>
      <Dialog
        component="form"
        onSubmit={handleSubmit}
        open={open}
        onClose={onClose}
      >
        <DialogTitle>{t('Co-applicant details')}</DialogTitle>
        <DialogContent sx={{ overflow: 'visible' }}>
          <Grid2 container spacing={1}>
            <Grid2
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <TextField
                fullWidth
                variant="outlined"
                onChange={handleChange}
                onBlur={handleBlur}
                label={t('First name')}
                name="firstName"
                autoComplete="given-name"
                value={firstName}
                error={touched.firstName && !isValid({ firstName })}
                helperText={
                  touched.firstName &&
                  !isValid({ firstName }) &&
                  t('Enter first name')
                }
              />
            </Grid2>
            <Grid2
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <TextField
                fullWidth
                variant="outlined"
                onChange={handleChange}
                onBlur={handleBlur}
                label={t('Last name')}
                name="lastName"
                autoComplete="family-name"
                value={lastName}
                error={touched.lastName && !isValid({ lastName })}
                helperText={
                  touched.lastName &&
                  !isValid({ lastName }) &&
                  t('Enter last name')
                }
              />
            </Grid2>
            <Grid2
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <TextField
                fullWidth
                variant="outlined"
                onChange={handleChange}
                onBlur={handleBlur}
                label={t('Personal number')}
                placeholder={t('yyyymmddnnnn')}
                name="personalNumber"
                type="tel"
                value={personalNumber}
                error={touched.personalNumber && !isValid({ personalNumber })}
                helperText={
                  touched.personalNumber &&
                  !isValid({ personalNumber }) &&
                  t('Enter social security number (yyyymmddnnnn)')
                }
              />
            </Grid2>
            <Grid2
              implementation="css"
              smDown
              component={Hidden}
              size={{
                sm: 6,
              }}
            />
            <Grid2
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <TextField
                fullWidth
                variant="outlined"
                onChange={handleChange}
                onBlur={handleBlur}
                label={t('Email address')}
                name="email"
                type="email"
                value={email}
                error={touched.email && !isValid({ email })}
                helperText={
                  touched.email &&
                  !isValid({ email }) &&
                  t('Enter email address')
                }
              />
            </Grid2>
            <Grid2
              size={{
                xs: 12,
                sm: 6,
              }}
            >
              <TextField
                fullWidth
                variant="outlined"
                onChange={handleChange}
                onBlur={handleBlur}
                label={t('Phone number')}
                name="phone"
                type="tel"
                value={phone}
                error={touched.phone && !isValid({ phone })}
                helperText={
                  touched.phone &&
                  !isValid({ phone }) &&
                  t('Enter phone number')
                }
              />
            </Grid2>
            <Grid2
              size={{
                xs: 12,
                sm: 5,
              }}
            >
              <TextField
                fullWidth
                variant="outlined"
                onChange={handleChange}
                onBlur={handleBlur}
                label={t('Street address')}
                name="street"
                value={street}
                error={touched.street && !isValid({ street })}
                helperText={
                  touched.street &&
                  !isValid({ street }) &&
                  t('Enter street address')
                }
              />
            </Grid2>
            <Grid2
              size={{
                xs: 12,
                sm: 3,
              }}
            >
              <TextField
                fullWidth
                variant="outlined"
                onChange={handleChange}
                onBlur={handleBlur}
                label={t('Postal code')}
                name="zip"
                type="tel"
                value={zip}
                error={touched.zip && !isValid({ zip })}
                helperText={
                  touched.zip && !isValid({ zip }) && t('Enter postal code')
                }
              />
            </Grid2>
            <Grid2
              size={{
                xs: 12,
                sm: 4,
              }}
            >
              <TextField
                fullWidth
                variant="outlined"
                onChange={handleChange}
                onBlur={handleBlur}
                label={t('City')}
                name="city"
                value={city}
                error={touched.city && !isValid({ city })}
                helperText={
                  touched.city && !isValid({ city }) && t('Enter city')
                }
              />
            </Grid2>
          </Grid2>
          {error?.message && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error.message}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('Cancel')}</Button>
          <Button loading={isPending} type="submit" onClick={handleSubmit}>
            {t('Save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Grid2>
  )
}
